import { useChat } from "@ai-sdk/vue";
import type { Message, Chat, Template, TemplateVersion } from "@prisma/client";
import { createIdGenerator } from "ai";
import { useSafeFetch } from "./useSafeFetch";
import type { FullTemplate } from "~/types/Templates/IFullTemplate";

let chatInstance: ReturnType<typeof useChat> | null = null;
const initialInput = ref("");
const initialPromptKey = ref<string | null>(null);
const model = ref<"openai" | "anthropic" | "google" | "anthropic_4">(
  "anthropic_4",
);
const uuid = ref("");
const json = ref();
const chats = ref<Chat[]>([]);
const selectedTemplate = ref<FullTemplate | null>(null);
const lastGeneratedTemplate = ref<FullTemplate | null>(null);
const templates = ref<Template[]>([]);
const templateVersion = ref<TemplateVersion | null>(null);

export const useAITemplates = () => {
  const createTemplate = async (content: string, messageId: string) => {
    return await useSafeFetch<FullTemplate>(
      `/api/chats/${uuid.value}/templates`,
      {
        method: "POST",
        body: {
          content,
          messageId,
        },
      },
      null,
    );
  };

  const updateTemplate = async (templateId: string, content: string) => {
    return await useSafeFetch<FullTemplate>(
      `/api/chats/${uuid.value}/templates/${templateId}`,
      {
        method: "PUT",
        body: {
          content,
        },
      },
      null,
    );
  };

  const getTemplate = async (templateId: string) => {
    if (!templateId) {
      throw new Error("Template ID is not set");
    }

    return await useSafeFetch<FullTemplate | null>(
      `/api/chats/${uuid.value}/templates/${templateId}`,
      {
        method: "GET",
      },
      null,
    );
  };

  const getTemplates = async () => {
    const data = await useSafeFetch<Template[]>(
      `/api/chats/${uuid.value}/templates`,
      {
        method: "GET",
      },
      null,
    );

    if (data) {
      templates.value = data;
    }

    return data;
  };

  const setTemplate = (fetchedTemplate: FullTemplate | null) => {
    selectedTemplate.value = fetchedTemplate;

    setTemplateVersion(fetchedTemplate ? fetchedTemplate.latest : null);
  };

  const setTemplateVersion = (version: TemplateVersion | null) => {
    templateVersion.value = version;
  };

  const getTemplateVersion = async (version: number) => {
    if (!selectedTemplate.value) {
      throw new Error("Template is not set");
    }

    if (version === undefined) {
      throw new Error("Version is not set");
    }
    return await useSafeFetch<TemplateVersion | null>(
      `/api/chats/${uuid.value}/templates/${selectedTemplate.value.id}/versions/${version}`,
      {
        method: "GET",
      },
      null,
    );
  };

  const changeTemplateVersion = (offset: number) => {
    if (!selectedTemplate.value) {
      throw new Error("Template is not set");
    }

    if (!templateVersion.value) {
      throw new Error("Template version is not set");
    }

    const newVersion = templateVersion.value.version + offset;

    if (newVersion < 1) {
      throw new Error("Cannot go back to a negative version");
    }

    const versionExists = selectedTemplate.value.versions.some(
      (v) => v.version === newVersion,
    );

    if (!versionExists) {
      console.warn(`Version ${newVersion} does not exist`);
      return;
    }

    getTemplateVersion(newVersion)
      .then((version) => {
        if (version) {
          setTemplateVersion(version);
        } else {
          throw new Error("Template version not found");
        }
      })
      .catch((error) => {
        console.error("Error fetching template version:", error);
      });
  };

  return {
    createTemplate,
    updateTemplate,
    getTemplate,
    getTemplates,
    selectedTemplate,
    lastGeneratedTemplate,
    templates,
    setTemplate,
    setTemplateVersion,
    templateVersion,
    getTemplateVersion,
    changeTemplateVersion,
  };
};

export const useAIChat = () => {
  const createChat = async (title?: string) => {
    const newChat = await useSafeFetch<Chat>(
      "/api/chats",
      {
        method: "POST",
        body: {
          title,
        },
      },
      null,
    );

    if (newChat) {
      chats.value.unshift(newChat);
    }

    return newChat;
  };

  const getMessage = async (chatId: string, messageId: string) => {
    if (!messageId || !chatId) {
      throw new Error("Chat ID or Message ID is not set");
    }

    return await useSafeFetch<Message | null>(
      `/api/chats/${chatId}/messages/${messageId}`,
      {},
      null,
    );
  };

  const getChat = async (chatId: string) => {
    if (!chatId) {
      throw new Error("Chat ID is not set");
    }

    return await useSafeFetch<Message[] | []>(`/api/chats/${chatId}`, {}, []);
  };

  const renameChat = async (chatId: string, title: string) => {
    if (!chatId) {
      throw new Error("Chat ID is not set");
    }

    return await useSafeFetch(
      `/api/chats/${chatId}/rename`,
      {
        method: "POST",
        body: {
          title,
        },
      },
      [],
    );
  };

  const fetchChats = async () => {
    const data = await useSafeFetch<Chat[]>(
      `/api/chats`,
      {
        method: "GET",
      },
      [],
    );

    chats.value = data || [];

    return data;
  };

  const deleteChat = async (chatId: string) => {
    if (!chatId) {
      throw new Error("Chat ID is not set");
    }

    const data = await useSafeFetch(
      `/api/chats/${chatId}`,
      {
        method: "DELETE",
      },
      null,
    );

    if (data !== null) {
      chats.value = chats.value.filter((chat) => chat.id !== chatId);
    }

    return data;
  };

  return {
    createChat,
    getChat,
    fetchChats,
    deleteChat,
    renameChat,
    getMessage,
    chats,
  };
};

export const useAI = () => {
  const { setUuid } = useAIUuid();

  setUuid();

  if (chatInstance && chatInstance.id === uuid.value) return chatInstance;

  if (chatInstance) {
    chatInstance.stop();
    chatInstance = null;
  }

  chatInstance = useChat({
    id: uuid.value,
    experimental_prepareRequestBody({ messages, id, requestBody }) {
      const body = requestBody as { initialPromptKey?: string };

      return {
        id,
        model: model.value,
        message: messages[messages.length - 1],
        mjml:
          lastGeneratedTemplate.value?.id !== selectedTemplate.value?.id
            ? selectedTemplate.value?.latest?.content
            : undefined,
        initialPromptKey: body?.initialPromptKey ?? null,
      };
    },
    sendExtraMessageFields: true,
    generateId: createIdGenerator({
      prefix: "msgc",
      size: 16,
    }),
    onError: (error) => {
      useBugsnag().notify("AI Error: " + error);
    },
  });

  return chatInstance;
};

export const useAIInput = () => {
  return {
    initialInput,
    initialPromptKey,
  };
};

export const useAIUuid = () => {
  const setUuid = () => {
    const route = useRoute();
    const param = route.params.uuid;

    if (!param || typeof param !== "string") {
      throw new Error("UUID parameter is missing or invalid");
    }
    uuid.value = param;
  };

  return {
    uuid,
    setUuid,
  };
};

export const useAIModel = () => {
  return {
    model,
  };
};

export const useAIJson = () => {
  const setJson = (data: string) => {
    json.value = data;
  };

  return {
    json,
    setJson,
  };
};
